import { useEffect, useState } from 'react';

import {
  AI_ASSISTANT_SEND_EXAMPLE_MESSAGE_EVENT,
  type AiAssistantExampleMessageDetail,
} from 'containers/admin/clients/client/tabs-components/ai-assistant/ai-assistant-events';
import type { MarkPromptSource } from 'containers/admin/clients/client/tabs-components/ai-assistant/ai-chat.types';

interface UseExampleMessageSenderArgs {
  conversationId: string | null;
  hasMessages: boolean;
  isReadOnly?: boolean;
  setSearchParams: (params: Record<string, string>) => void;
  sendMessage: (message: string, source?: MarkPromptSource) => void;
}

/**
 * Bridges the example-message event (dispatched from UI mounted outside the
 * chat render tree, e.g. the walkthrough modal) into the chat's send flow.
 *
 * On the event it resets to a fresh conversation (`setSearchParams({})`), then
 * waits until that reset has applied (no `conversationId`, no messages) before
 * sending, so the example always lands as the first message of a new chat.
 */
const useExampleMessageSender = ({
  conversationId,
  hasMessages,
  isReadOnly,
  setSearchParams,
  sendMessage,
}: UseExampleMessageSenderArgs): void => {
  const [pendingExampleMessage, setPendingExampleMessage] = useState<
    string | null
  >(null);

  useEffect(() => {
    const handleExampleMessage = (event: Event): void => {
      if (isReadOnly) return;

      const detail = (
        event instanceof CustomEvent ? event.detail ?? {} : {}
      ) as Partial<AiAssistantExampleMessageDetail>;
      const message =
        typeof detail.message === 'string' ? detail.message.trim() : '';

      if (!message) return;

      setSearchParams({});
      setPendingExampleMessage(message);
    };

    window.addEventListener(
      AI_ASSISTANT_SEND_EXAMPLE_MESSAGE_EVENT,
      handleExampleMessage
    );

    return () => {
      window.removeEventListener(
        AI_ASSISTANT_SEND_EXAMPLE_MESSAGE_EVENT,
        handleExampleMessage
      );
    };
  }, [isReadOnly, setSearchParams]);

  useEffect(() => {
    if (pendingExampleMessage == null) return;
    if (conversationId != null || hasMessages) return;

    const message = pendingExampleMessage;

    setPendingExampleMessage(null);
    sendMessage(message, 'example');
  }, [pendingExampleMessage, conversationId, hasMessages, sendMessage]);
};

export default useExampleMessageSender;
